import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { adminApi, errMsg, PLACEHOLDER } from "@/lib/api";
import { ProductPicker } from "@/admin/ProductPicker";
import { Btn, Loading, PageHead, Panel, RowTools, SaveBtn, inputCls, move, useSiteDraft } from "@/admin/ui";

function MoodMembers({ mood }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    adminApi.get("/products", { params: { mood: mood.id, limit: 200 } }).then((r) => setItems(r.data.items));
  }, [mood.id]);
  useEffect(() => {
    load();
  }, [load]);
  const call = async (path, ids) => {
    try {
      await adminApi.post(`/moods/${mood.id}/${path}`, { product_ids: ids });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel data-testid="mood-members">
      <p className="font-head font-bold text-lg mb-1">Records in “{mood.name}”</p>
      <p className="text-xs text-zinc-500 mb-4">{items.length} records · changes save instantly</p>
      <ProductPicker exclude={items.map((p) => p.id)} onPick={(p) => call("add", [p.id])} testid="mood-picker" />
      <ul className="mt-4 divide-y divide-zinc-100">
        {items.map((p) => (
          <li key={p.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`mood-member-${p.sku}`}>
            <img src={p.image || PLACEHOLDER} alt="" className="w-9 h-9 object-cover border border-zinc-200" />
            <span className="flex-1 truncate"><b className="font-arial text-xs uppercase">{p.artist}</b> – {p.title}</span>
            <button onClick={() => call("remove", [p.id])} className="text-zinc-400 hover:text-red" data-testid={`mood-remove-${p.sku}`}><X className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export default function Moods() {
  const { draft, patch, save, saving } = useSiteDraft(["mood_groups"]);
  const [active, setActive] = useState(null);
  if (!draft) return <Loading />;
  const groups = draft.mood_groups;
  const set = (v) => patch("mood_groups", v);
  const setGroup = (gi, g) => set(groups.map((x, j) => (j === gi ? g : x)));
  return (
    <div>
      <PageHead title="Shop by mood" desc="Organise moods into groups for the menu, then choose which records sit in each mood. Save new moods before adding records.">
        <SaveBtn save={save} saving={saving} />
      </PageHead>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-4">
          {groups.map((g, gi) => (
            <Panel key={g.id || gi} data-testid={`mood-group-${gi}`}>
              <div className="flex gap-2 items-center">
                <input value={g.name} onChange={(e) => setGroup(gi, { ...g, name: e.target.value })} className={`${inputCls} font-bold`} data-testid={`mood-group-name-${gi}`} />
                <RowTools i={gi} len={groups.length} onMove={(d) => set(move(groups, gi, d))} onDelete={() => set(groups.filter((_, j) => j !== gi))} testid={`mood-group-${gi}`} />
              </div>
              <div className="mt-3 space-y-2 pl-4 border-l-2 border-zinc-200">
                {g.moods.map((m, mi) => (
                  <div key={m.id || mi} className="flex gap-2 items-center">
                    <input value={m.name} onChange={(e) => setGroup(gi, { ...g, moods: g.moods.map((x, j) => (j === mi ? { ...x, name: e.target.value } : x)) })} className={inputCls} data-testid={`mood-name-${gi}-${mi}`} />
                    {m.id && <Btn variant={active?.id === m.id ? "dark" : "outline"} className="h-10 px-3 shrink-0" onClick={() => setActive(m)} data-testid={`mood-records-${m.id}`}>Records</Btn>}
                    <RowTools i={mi} len={g.moods.length} onMove={(d) => setGroup(gi, { ...g, moods: move(g.moods, mi, d) })} onDelete={() => setGroup(gi, { ...g, moods: g.moods.filter((_, j) => j !== mi) })} testid={`mood-${gi}-${mi}`} />
                  </div>
                ))}
                <button onClick={() => setGroup(gi, { ...g, moods: [...g.moods, { name: "New mood" }] })} className="text-xs font-bold flex items-center gap-1 hover:text-red" data-testid={`mood-add-${gi}`}><Plus className="w-3.5 h-3.5" /> Add mood</button>
              </div>
            </Panel>
          ))}
          <Btn variant="outline" onClick={() => set([...groups, { name: "New group", moods: [] }])} data-testid="mood-group-add"><Plus className="w-4 h-4" /> Add group</Btn>
        </div>
        {active ? <MoodMembers key={active.id} mood={active} /> : <Panel className="text-sm text-zinc-500">Pick a mood's “Records” button to manage which records sit in it.</Panel>}
      </div>
    </div>
  );
}
